import { api } from '@/lib/api';
import type {
  Project,
  ProjectDetail,
  ProjectUpdate,
  ProjectStatusChange,
  ProjectClose,
  ProjectListResponse,
  ProjectsByStatusResponse,
  ProjectStatsResponse,
  ProjectStatus,
  WebsiteProjectUpdate,
} from '@/types';

// Projects API
export const projectsApi = {
  // List projects with pagination
  list: async (params?: {
    page?: number;
    per_page?: number;
    status?: ProjectStatus;
    customer_id?: string;
    assigned?: string;
  }): Promise<ProjectListResponse> => {
    const { data } = await api.get('/projects', { params });
    return data;
  },

  // List projects grouped by status
  listByStatus: async (): Promise<ProjectsByStatusResponse> => {
    const { data } = await api.get('/projects/by-status');
    return data;
  },

  // Get single project with history
  get: async (id: string): Promise<ProjectDetail> => {
    const { data } = await api.get(`/projects/${id}`);
    return data;
  },

  // Kein create: Projekte entstehen ausschliesslich ueber POST /projects/intake
  // aus dem CRM-Handoff, der Gebaeude und Projekt gemeinsam anlegt.

  // Update project
  update: async (id: string, project: ProjectUpdate): Promise<Project> => {
    const { data } = await api.put(`/projects/${id}`, project);
    return data;
  },

  // Change project status.
  // PATCH, nicht PUT — das Backend registriert diesen Endpunkt als PATCH, ein
  // PUT lief in 405 und der Annehmen-Button auf "Meine Arbeit" tat nichts.
  changeStatus: async (id: string, change: ProjectStatusChange): Promise<Project> => {
    const { data } = await api.patch(`/projects/${id}/status`, change);
    return data;
  },

  // Close project (won/lost)
  close: async (id: string, close: ProjectClose): Promise<Project> => {
    const { data } = await api.put(`/projects/${id}/close`, close);
    return data;
  },

  // Keine Historie: der Statusverlauf wurde beim Rueckbau bewusst gestrichen
  // ("process-sparse"), GET /projects/{id}/history gibt es nicht mehr.

  // Claim project from pool (self-assign)
  claim: async (projectId: string): Promise<Project> => {
    const { data } = await api.post(`/projects/${projectId}/claim`);
    return data;
  },

  // Assign project to a user (or null to return to pool)
  assign: async (projectId: string, userId: string | null): Promise<Project> => {
    const { data } = await api.post(`/projects/${projectId}/assign`, { user_id: userId });
    return data;
  },

  // Delete project
  delete: async (id: string): Promise<void> => {
    await api.delete(`/projects/${id}`);
  },

  // Update website visibility
  updateWebsiteSettings: async (id: string, settings: WebsiteProjectUpdate): Promise<Project> => {
    const { data } = await api.put(`/projects/${id}`, settings);
    return data;
  },

  // List projects for website management (completed projects)
  listForWebsite: async (): Promise<Project[]> => {
    // 'beauftragt' ist der kanonische Abschluss-Status. Vorher stand hier 'won' —
    // kein gueltiger Wert der Backend-Enum, das Backend antwortete mit 422 und
    // die Seite blieb dauerhaft leer.
    const { data } = await api.get('/projects', {
      params: { status: 'beauftragt', per_page: 100 },
    });
    return data.items;
  },
};

// Dashboard API
export const dashboardApi = {
  getStats: async (): Promise<ProjectStatsResponse> => {
    const { data } = await api.get('/dashboard');
    return data;
  },
};
